import type { PerspectiveCamera, Scene } from 'three/webgpu';
import {
  CROUCH_EYE_HEIGHT,
  PLAYER_EYE_HEIGHT,
  PRONE_EYE_HEIGHT,
  SnapFlags,
  Stance,
  WEAPONS,
  clamp,
  lerp,
  lerpAngle,
} from '@stillsun/shared';
import type { Vec3 } from '@stillsun/shared';
import {
  copyPose,
  newPoseSample,
  type GhostSlot,
  type PoseSample,
  type RemoteManager,
} from './remote.js';
import type { Hud } from '../ui/hud.js';

/** How much of the killer's life we replay, and how long we then sit on the last frame. */
const REPLAY_MS = 2500;
const HOLD_MS = 1500;
/** Total wall-clock a kill cam owns the camera for; the death countdown waits this out. */
export const KILLCAM_TOTAL_MS = REPLAY_MS + HOLD_MS;

/**
 * Local pose ring. Sized in *time*, not frames: the recorder throttles to one sample per
 * LOCAL_MIN_STEP_MS so a 144 Hz display covers the same span as a 60 Hz one, and 192
 * samples is ~3 s either way.
 */
const LOCAL_RING = 192;
const LOCAL_MIN_STEP_MS = 16;

/** Eye height while downed — mirrors main.ts's DOWNED_EYE_HEIGHT. */
const DOWNED_EYE_HEIGHT = 0.4;
/** Exponential rate the replayed eye height settles at across a stance change. */
const EYE_EASE = 12;

export interface KillCamOptions {
  killerId: number;
  killerName: string;
  /** The local player's name — it goes on the ghost's nameplate. */
  victimName: string;
  /** Where we fell. The authority on the kill position for the distance readout. */
  victimFeet: Vec3;
  /** `DeathEvent.weaponId`; looked up in the shared weapon table. */
  weaponId: number;
  /** From the `ShotEvent` in the same batch (see main.ts's `headshotIn`). */
  headshot: boolean;
}

function eyeOfPose(pose: PoseSample): number {
  if ((pose.flags & SnapFlags.Downed) !== 0) return DOWNED_EYE_HEIGHT;
  if (pose.stance === Stance.Prone) return PRONE_EYE_HEIGHT;
  if (pose.stance === Stance.Crouch) return CROUCH_EYE_HEIGHT;
  return PLAYER_EYE_HEIGHT;
}

/**
 * Replays the killer's last {@link REPLAY_MS} from their own eyes, with the local player
 * drawn back into the world as a capsule ghost, then holds on the kill for
 * {@link HOLD_MS} behind a card naming who, with what, and from how far.
 *
 * It owns the camera while `active` — main.ts skips its own camera write — and nothing
 * else: input stays locked by the death path, the view model stays hidden because the
 * player is dead, and `abort()` puts the world back the way it found it.
 *
 * Clock note: the killer's poses carry the server's snapshot stamps, while the local
 * ring carries `net.serverNow()` at the frame it was predicted — which runs roughly half
 * an RTT ahead of the server's view of us. The ghost therefore sits a few tens of
 * centimetres off where the killer actually saw it. Correcting that would need a
 * server-stamped local history the protocol does not carry, and at replay distances it
 * is not visible.
 */
export class KillCam {
  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private readonly remotes: RemoteManager;
  private readonly hud: Hud;
  /** Called once the hold expires and the normal death flow should take back over. */
  private readonly onFinish: () => void;

  // --- the local player's own recorded history -------------------------------
  private readonly localRing: PoseSample[] = [];
  private localWrites = 0;

  // --- replay buffers, copied by value at start() ----------------------------
  private readonly killerBuf: PoseSample[] = [];
  private readonly localBuf: PoseSample[] = [];
  private killerCount = 0;
  private localCount = 0;
  private killerCursor = 0;
  private localCursor = 0;

  // --- scratch, reused every frame -------------------------------------------
  private readonly killerPose = newPoseSample();
  private readonly localPose = newPoseSample();

  // --- run state --------------------------------------------------------------
  private running = false;
  private holding = false;
  private elapsed = 0;
  private holdLeft = 0;
  private startT = 0;
  private killerId = 0;
  private ghost: GhostSlot | null = null;
  private eyeHeight = PLAYER_EYE_HEIGHT;
  private eyePrimed = false;

  constructor(
    scene: Scene,
    camera: PerspectiveCamera,
    remotes: RemoteManager,
    hud: Hud,
    onFinish: () => void,
  ) {
    this.scene = scene;
    this.camera = camera;
    this.remotes = remotes;
    this.hud = hud;
    this.onFinish = onFinish;
    for (let i = 0; i < LOCAL_RING; i++) this.localRing.push(newPoseSample());
  }

  get active(): boolean {
    return this.running;
  }

  /**
   * Records one local pose. Called every frame from the frame loop; throttled internally
   * to LOCAL_MIN_STEP_MS and never allocates. `t` is `net.serverNow()`.
   */
  recordLocal(
    t: number,
    pos: Vec3,
    yaw: number,
    pitch: number,
    stance: number,
    flags: number,
  ): void {
    if (this.localWrites > 0) {
      const last = this.localRing[(this.localWrites - 1) % LOCAL_RING];
      if (last && t - last.t < LOCAL_MIN_STEP_MS) return;
    }
    const s = this.localRing[this.localWrites % LOCAL_RING];
    if (!s) return;
    s.t = t;
    s.x = pos.x;
    s.y = pos.y;
    s.z = pos.z;
    s.yaw = yaw;
    s.pitch = pitch;
    s.stance = stance;
    s.flags = flags;
    this.localWrites += 1;
  }

  /** Drops every recorded pose — a new raid is not a continuation of the last one. */
  resetHistory(): void {
    this.localWrites = 0;
  }

  /**
   * Starts a replay. Returns false when the killer has no snapshot history at all (they
   * were never in a snapshot we kept), in which case the caller should fall straight
   * through to the ordinary death screen.
   */
  start(opts: KillCamOptions): boolean {
    if (this.running) this.abort();

    const endT = this.remotes.latestPoseTime(opts.killerId);
    if (endT <= 0) return false;
    const startT = endT - REPLAY_MS;

    this.killerCount = this.remotes.historyOf(opts.killerId, startT, this.killerBuf);
    if (this.killerCount === 0) return false;
    this.localCount = this.copyLocalHistory(startT);

    const last = this.killerBuf[this.killerCount - 1];
    const dist = last
      ? Math.hypot(
          last.x - opts.victimFeet.x,
          last.y - opts.victimFeet.y,
          last.z - opts.victimFeet.z,
        )
      : 0;

    this.running = true;
    this.holding = false;
    this.elapsed = 0;
    this.holdLeft = HOLD_MS;
    this.startT = startT;
    this.killerId = opts.killerId;
    this.killerCursor = 0;
    this.localCursor = 0;
    this.eyePrimed = false;

    // Their body is where our camera is about to be; ours is missing from the world.
    this.remotes.setHidden(opts.killerId, true);
    // Our own body, put back into the world as a crowd instance so the killer's replay has
    // somebody to have shot. The crowd owns it; there is nothing to add to the scene.
    if (this.localCount > 0) this.ghost = this.remotes.createGhost(opts.victimName);

    // WEAPON_NONE (0) has no table entry, and the id is off the wire either way.
    const weapon = WEAPONS[opts.weaponId]?.name ?? 'unknown';
    this.hud.hideDeath();
    this.hud.showKillCam(opts.killerName, weapon, dist, opts.headshot);
    return true;
  }

  /** Advances the replay and writes the camera. Allocation-free. */
  update(dt: number): void {
    if (!this.running) return;
    const ms = dt * 1000;

    if (this.holding) {
      this.holdLeft -= ms;
    } else {
      this.elapsed += ms;
      if (this.elapsed >= REPLAY_MS) {
        this.elapsed = REPLAY_MS;
        this.holding = true;
      }
    }

    const t = this.startT + this.elapsed;
    this.killerCursor = poseAt(
      this.killerBuf,
      this.killerCount,
      this.killerCursor,
      t,
      this.killerPose,
    );

    const targetEye = eyeOfPose(this.killerPose);
    if (!this.eyePrimed) {
      this.eyeHeight = targetEye;
      this.eyePrimed = true;
    } else {
      this.eyeHeight += (targetEye - this.eyeHeight) * Math.min(1, dt * EYE_EASE);
    }
    this.camera.position.set(
      this.killerPose.x,
      this.killerPose.y + this.eyeHeight,
      this.killerPose.z,
    );
    this.camera.rotation.set(this.killerPose.pitch, this.killerPose.yaw, 0, 'YXZ');

    if (this.ghost && this.localCount > 0) {
      this.localCursor = poseAt(
        this.localBuf,
        this.localCount,
        this.localCursor,
        t,
        this.localPose,
      );
      this.ghost.setPose(
        this.localPose.x,
        this.localPose.y,
        this.localPose.z,
        this.localPose.yaw,
        this.localPose.anim,
      );
    }

    this.hud.setKillCamProgress(this.elapsed / REPLAY_MS);

    if (this.holding && this.holdLeft <= 0) {
      this.teardown();
      this.onFinish();
    }
  }

  /**
   * Stops immediately and puts everything back — respawn, raid over, kick, hub. Never
   * calls `onFinish`: whatever aborted us owns the screen now.
   */
  abort(): void {
    this.teardown();
  }

  private teardown(): void {
    if (!this.running) return;
    this.running = false;
    this.holding = false;
    if (this.ghost) {
      this.ghost.dispose();
      this.ghost = null;
    }
    this.remotes.setHidden(this.killerId, false);
    this.killerId = 0;
    this.hud.hideKillCam();
  }

  /** The local ring's tail, copied by value so the recorder can keep writing behind us. */
  private copyLocalHistory(sinceMs: number): number {
    const n = Math.min(this.localWrites, LOCAL_RING);
    const base = this.localWrites - n;
    let count = 0;
    for (let i = 0; i < n; i++) {
      const src = this.localRing[(base + i) % LOCAL_RING];
      if (!src || src.t < sinceMs) continue;
      let dst = this.localBuf[count];
      if (!dst) {
        dst = newPoseSample();
        this.localBuf.push(dst);
      }
      copyPose(src, dst);
      count += 1;
    }
    return count;
  }
}

/**
 * Interpolates `buf` at server time `t` into `out`, walking forward from `cursor` (the
 * playhead only ever moves forward, so this is O(1) amortised). Clamps at both ends —
 * a killer whose history is shorter than the replay holds their earliest pose rather
 * than being extrapolated backwards into the floor. Returns the new cursor.
 */
function poseAt(
  buf: readonly PoseSample[],
  count: number,
  cursor: number,
  t: number,
  out: PoseSample,
): number {
  if (count === 0) return 0;
  let i = Math.min(cursor, count - 1);
  while (i + 1 < count) {
    const nx = buf[i + 1];
    if (!nx || nx.t > t) break;
    i += 1;
  }
  const a = buf[i];
  if (!a) return i;
  const b = i + 1 < count ? buf[i + 1] : undefined;
  if (!b || t <= a.t) {
    copyPose(a, out);
    return i;
  }
  const span = b.t - a.t;
  const k = span > 0 ? clamp((t - a.t) / span, 0, 1) : 0;
  out.t = t;
  out.x = lerp(a.x, b.x, k);
  out.y = lerp(a.y, b.y, k);
  out.z = lerp(a.z, b.z, k);
  out.yaw = lerpAngle(a.yaw, b.yaw, k);
  out.pitch = lerp(a.pitch, b.pitch, k);
  // Nearest sample, not a lerp: an animation id and a flag word have no midpoint.
  out.anim = k < 0.5 ? a.anim : b.anim;
  out.stance = k < 0.5 ? a.stance : b.stance;
  out.flags = k < 0.5 ? a.flags : b.flags;
  return i;
}
